import { Injectable } from '@angular/core';
import { ConversationService } from './conversation.service';
import { MessagingService } from './messaging.service';
import { Chat, Message, MessageAttachment } from '../models/message.model';
import { ViewChild, ElementRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  constructor(
    private conversationService: ConversationService,
    private messagingService: MessagingService,
    private snackBar: MatSnackBar,
  ) {}

  chats: Chat[] = [];

  private conversationSubject = new BehaviorSubject<Chat[]>([]);
  private selectedChatSubject = new BehaviorSubject<Chat | null>(null);
  private onlineUserIdsSubject = new BehaviorSubject<Set<number>>(new Set());
  private sendFailedSubject = new Subject<string>();
  private widgetOpenSubject = new BehaviorSubject<boolean>(false);
  private selectedChat?: Chat;
  private socketListenersInitialized = false;
  private currentUserId?: number;

  selectedChat$ = this.selectedChatSubject.asObservable();
  conversations$ = this.conversationSubject.asObservable();
  onlineUserIds$ = this.onlineUserIdsSubject.asObservable();
  /** Emits the original text of a message whose send failed, so the
   *  composer can restore it instead of leaving it lost. */
  sendFailed$ = this.sendFailedSubject.asObservable();
  /** Whether the floating messaging widget is expanded. Lives here rather
   *  than on MessagingWidget itself because that component is destroyed
   *  and recreated on every page navigation (each page embeds its own
   *  instance) — tracking it on this singleton lets the widget stay open
   *  across navigation instead of resetting to closed on every page. */
  widgetOpen$ = this.widgetOpenSubject.asObservable();

  setWidgetOpen(open: boolean): void {
    this.widgetOpenSubject.next(open);
  }

  /* User Controls for the list of chats user has created */
  /**
   *
   * @param currentUserId
   * @returns
   */
  reloadConversations(currentUserId: number): void {
    if (!currentUserId) {
      console.warn('No current user Id, cannot reload conversations');
      return;
    }

    this.conversationService.getConversations(currentUserId).subscribe({
      next: (chats) => {
        const normalizedChats = chats.map((chat) => ({
          ...chat,
          messages: chat.messages || [],
          lastMessage: chat.lastMessage,
          unread: Boolean(chat.unread),
          muted: Boolean(chat.muted),
          typingUserIds: [],
        }));

        this.conversationSubject.next(this.sortChatsByTime(normalizedChats));
      },
      error: (err) => {
        console.error('Failed to reload conversations', err.message);
      },
    });
  }

  selectChat(chat: Chat, currentUserId: number): void {
    this.messagingService.joinConversation(chat.id);

    this.conversationService.getMessages(chat.id, currentUserId).subscribe((messages) => {
      const fetchedMessages = messages.map((msg) => ({
        ...msg,
        fromMe: msg.sender_id === currentUserId,
      }));

      // If this chat is already open (e.g. a re-select racing a live
      // socket update), merge instead of replacing outright — this fetch
      // can resolve with a snapshot older than a message:receive that
      // already landed while it was in flight.
      const isAlreadyOpen = this.selectedChat?.id === chat.id;
      const mergedMessages = isAlreadyOpen
        ? this.mergeMessages(fetchedMessages, this.selectedChat!.messages)
        : fetchedMessages;

      const selectedChat = {
        ...chat,
        unread: false,
        messages: mergedMessages,
      };
      this.selectedChat = selectedChat;
      this.selectedChatSubject.next(selectedChat);

      // selectedChat above only clears unread on the thread pane's own copy
      // — the sidebar/widget list (and its avatar dot) render off this
      // separate array, which selecting a chat otherwise never touches.
      this.conversationSubject.next(
        this.conversationSubject.value.map((c) => (c.id === chat.id ? { ...c, unread: false } : c)),
      );

      this.conversationService.markConversationRead(chat.id, currentUserId).subscribe({
        error: (err) => console.error(err),
      });
    });
  }

  /**
   * Clears unread on the open chat when it's already on screen but still
   * flagged (e.g. a notification arrived while it was selected elsewhere, then
   * the user came back). selectChat() is the only other place that marks a
   * conversation read, and it doesn't run when the chat is already selected.
   */
  markSelectedChatRead(currentUserId: number): void {
    const id = this.selectedChat?.id;
    if (!id) return;

    const entry = this.conversationSubject.value.find((c) => c.id === id);
    if (!entry?.unread) return;

    this.conversationSubject.next(
      this.conversationSubject.value.map((c) => (c.id === id ? { ...c, unread: false } : c)),
    );
    this.conversationService.markConversationRead(id, currentUserId).subscribe({
      error: (err) => console.error(err),
    });
  }

  /**
   * Clears the selected chat and leaves its room. `selectedChat` is shared
   * (this service is a singleton), so anything that selects a chat must
   * call this on teardown — otherwise the next thing to read `selectedChat$`
   * (e.g. a different page, or the messaging widget) sees a stale selection
   * left over from whoever had one open last, rather than starting clean.
   */
  closeSelectedChat(): void {
    if (this.selectedChat) {
      this.messagingService.leaveConversation(this.selectedChat.id);
    }

    this.selectedChat = undefined;
    this.selectedChatSubject.next(null);
  }

  /**
   * Merges a freshly-fetched message snapshot with whatever's already
   * loaded, instead of blindly replacing it. A REST refetch (on reconnect,
   * or re-selecting an already-open chat) can resolve after a live
   * message:receive event already appended something newer — without this,
   * the older snapshot would silently erase it. Anything in `existing` the
   * fetch doesn't know about yet is kept; the fetch is the source of truth
   * for any message it DOES include (e.g. edits/deletes reflected there).
   * @param {Message[]} fetched - The just-fetched message snapshot.
   * @param {Message[]} existing - Messages already loaded for this chat.
   * @returns {Message[]}
   */
  private mergeMessages(fetched: Message[], existing: Message[]): Message[] {
    const fetchedIds = new Set(fetched.map((message) => message.id));
    const liveOnly = existing.filter((message) => !fetchedIds.has(message.id));

    return [...fetched, ...liveOnly].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }

  // Swaps in fresh search-match details without touching the loaded messages.
  updateSelectedChatMatches(matches: {
    matchCount?: number;
    matchingMessageIds?: number[];
    matchSnippet?: string;
  }): void {
    if (!this.selectedChat) {
      return;
    }

    this.selectedChat = {
      ...this.selectedChat,
      matchCount: matches.matchCount,
      matchingMessageIds: matches.matchingMessageIds,
      matchSnippet: matches.matchSnippet,
    };

    this.selectedChatSubject.next(this.selectedChat);
  }

  private sortChatsByTime(chats: Chat[]): Chat[] {
    return [...chats].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime());
  }

  /* Selected Chat functions */
  /**
   * Sends over REST rather than the socket, so the message is durably
   * saved (or cleanly fails) regardless of the socket's connection state —
   * a socket-only send queued in-memory during a disconnect is lost for
   * good if the tab closes before it reconnects. The socket is still what
   * delivers it live to other members; this only changes how the sender's
   * own copy gets persisted.
   *
   * A locally-tagged "pending" copy only appears if the request is still
   * in flight after PENDING_MESSAGE_DELAY_MS — fast responses (a normal
   * send, or a quick rejection like profanity) resolve well under that,
   * so nothing ever flashes on screen. A genuinely slow or stuck send
   * (e.g. mid-disconnect) crosses the threshold and shows "Sending…" for
   * as long as it's actually pending, which is the case that matters.
   *
   * Either way, the pending copy (if it was ever shown) is reconciled
   * once the request resolves. On failure (profanity rejection, disabled
   * conversation, network error, etc.) it's removed and the reason
   * surfaced via snackbar — same pattern as renameConversation — rather
   * than left sitting in the chat.
   */
  private static readonly PENDING_MESSAGE_DELAY_MS = 300;

  sendMessage(
    content: string,
    currentUserId: number,
    attachments?: MessageAttachment[],
    replyTo?: Message,
  ): void {
    if (!this.selectedChat) {
      return;
    }

    const trimmed = content.trim();

    if (!trimmed && !attachments?.length) {
      return;
    }

    const chatId = this.selectedChat.id;
    const tempId = -Date.now();

    const pendingTimer = setTimeout(() => {
      this.appendLocalMessage(chatId, {
        id: tempId,
        conversation_id: chatId,
        sender_id: currentUserId,
        content: trimmed,
        is_deleted: false,
        created_at: new Date(),
        fromMe: true,
        status: 'pending',
        attachments,
        replyTo: replyTo
          ? { id: replyTo.id, content: replyTo.content, is_deleted: replyTo.is_deleted, sender: replyTo.sender }
          : undefined,
      });
    }, ChatService.PENDING_MESSAGE_DELAY_MS);

    this.conversationService
      .sendMessage({
        conversation_id: chatId,
        sender_id: currentUserId,
        content: trimmed,
        attachments,
        reply_to_message_id: replyTo?.id,
      })
      .subscribe({
        next: (message) => {
          clearTimeout(pendingTimer);
          this.resolvePendingMessage(chatId, tempId, { ...message, fromMe: true });
        },
        error: (err) => {
          clearTimeout(pendingTimer);
          this.removeLocalMessage(chatId, tempId);
          this.snackBar.open(err?.error?.error || 'Failed to send message.', 'Dismiss', {
            duration: 5000,
          });
          this.sendFailedSubject.next(trimmed);
        },
      });
  }

  private appendLocalMessage(chatId: number, message: Message): void {
    if (!this.selectedChat || this.selectedChat.id !== chatId) {
      return;
    }

    const updatedChat = {
      ...this.selectedChat,
      messages: [...this.selectedChat.messages, message],
    };

    this.selectedChat = updatedChat;
    this.selectedChatSubject.next(updatedChat);
  }

  private resolvePendingMessage(chatId: number, tempId: number, message: Message): void {
    if (!this.selectedChat || this.selectedChat.id !== chatId) {
      return;
    }

    const withoutPending = this.selectedChat.messages.filter((m) => m.id !== tempId);

    // The real message may have already arrived live via message:receive
    // (the server broadcasts to the sender's own conversation room too)
    // while this REST call was still in flight — don't duplicate it.
    const alreadyArrivedLive = withoutPending.some((m) => m.id === message.id);

    const updatedChat = {
      ...this.selectedChat,
      messages: alreadyArrivedLive ? withoutPending : [...withoutPending, message],
    };

    this.selectedChat = updatedChat;
    this.selectedChatSubject.next(updatedChat);
  }

  private removeLocalMessage(chatId: number, tempId: number): void {
    if (!this.selectedChat || this.selectedChat.id !== chatId) {
      return;
    }

    const updatedChat = {
      ...this.selectedChat,
      messages: this.selectedChat.messages.filter((m) => m.id !== tempId),
    };

    this.selectedChat = updatedChat;
    this.selectedChatSubject.next(updatedChat);
  }

  initializeSocketListeners(currentUserId: number): void {
    this.currentUserId = currentUserId;
    if (this.socketListenersInitialized) {
      return;
    }

    this.socketListenersInitialized = true;

    this.messagingService.onConversationUpdated().subscribe((update) => {
      const chats = this.conversationSubject.value;

      const existingChat = chats.find((chat) => Number(chat.id) === Number(update.conversation_id));

      if (!existingChat) {
        if (!this.currentUserId) {
          return;
        }
        this.reloadConversations(this.currentUserId);
        return;
      }

      // Mirrors the server's own unread rule (getUserConversations /
      // sendMessage's notify() skip): stays read if you're the one who sent
      // it, or if you already have this exact conversation open — otherwise
      // this live update is the only thing that will ever mark it unread,
      // since the dot's initial value only reaches the client on the next
      // full conversations fetch.
      const isViewingThisChat = this.selectedChat?.id === update.conversation_id;
      const isOwnMessage = Number(update.sender_id) === Number(this.currentUserId);

      const updatedChats = this.conversationSubject.value.map((chat) =>
        Number(chat.id) === Number(update.conversation_id)
          ? {
              ...chat,
              lastMessage: update.lastMessage,
              time: update.time,
              unread: !isViewingThisChat && !isOwnMessage,
            }
          : chat,
      );
      this.conversationSubject.next(this.sortChatsByTime(updatedChats));
    });

    this.messagingService.onTyping().subscribe(({ conversation_id, sender_id, isTyping }) => {
      // Tracked per-sender (not a single flag) so one member going quiet in
      // a group conversation doesn't clear another member's still-active
      // typing indicator.
      const applyTyping = (chat: Chat): Chat => {
        if (Number(chat.id) !== Number(conversation_id)) return chat;

        const typingUserIds = new Set(chat.typingUserIds ?? []);
        if (isTyping) {
          typingUserIds.add(Number(sender_id));
        } else {
          typingUserIds.delete(Number(sender_id));
        }

        return { ...chat, typingUserIds: Array.from(typingUserIds) };
      };

      this.conversationSubject.next(this.conversationSubject.value.map(applyTyping));

      if (this.selectedChat && Number(this.selectedChat.id) === Number(conversation_id)) {
        this.selectedChat = applyTyping(this.selectedChat);
        this.selectedChatSubject.next(this.selectedChat);
      }
    });

    this.messagingService.onMessageEdited().subscribe((update) => {
      if (!this.selectedChat) {
        return;
      }

      const updatedMessages = (this.selectedChat.messages || []).map((message) => {
        if (Number(message.id) === Number(update.message_id)) {
          return {
            ...message,
            content: update.content,
            original_content: update.original_content,
            is_edited: update.is_edited,
            edited_at: update.edited_at,
          };
        }

        if (message.replyTo && Number(message.replyTo.id) === Number(update.message_id)) {
          return {
            ...message,
            replyTo: { ...message.replyTo, content: update.content },
          };
        }

        return message;
      });

      this.selectedChat = {
        ...this.selectedChat,
        messages: updatedMessages,
      };

      this.selectedChatSubject.next(this.selectedChat);
    });

    // Neither of these was previously wired up here — messages.ts/
    // counselor-msg.ts had no way to learn a conversation became
    // disabled/enabled while open, despite already rendering UI based on
    // selectedChat.is_disabled. Handling it centrally fixes that for every
    // consumer of this service, not just whichever one happened to add its
    // own listener.
    this.messagingService.onConversationDisabled().subscribe(({ conversation_id }) => {
      const updatedChats = this.conversationSubject.value.map((chat) =>
        Number(chat.id) === Number(conversation_id) ? { ...chat, is_disabled: true } : chat,
      );
      this.conversationSubject.next(updatedChats);

      if (this.selectedChat && Number(this.selectedChat.id) === Number(conversation_id)) {
        this.selectedChat = { ...this.selectedChat, is_disabled: true };
        this.selectedChatSubject.next(this.selectedChat);
      }
    });

    this.messagingService.onConversationEnabled().subscribe(({ conversation_id }) => {
      const updatedChats = this.conversationSubject.value.map((chat) =>
        Number(chat.id) === Number(conversation_id) ? { ...chat, is_disabled: false } : chat,
      );
      this.conversationSubject.next(updatedChats);

      if (this.selectedChat && Number(this.selectedChat.id) === Number(conversation_id)) {
        this.selectedChat = { ...this.selectedChat, is_disabled: false };
        this.selectedChatSubject.next(this.selectedChat);
      }
    });

    this.messagingService.onMessageDeleted().subscribe((update) => {
      if (!this.selectedChat) {
        return;
      }

      const updatedMessages = (this.selectedChat.messages || []).map((message) => {
        if (Number(message.id) === Number(update.message_id)) {
          return {
            ...message,
            is_deleted: true,
            deleted_at: update.deleted_at,
            content: 'Message deleted',
          };
        }

        if (message.replyTo && Number(message.replyTo.id) === Number(update.message_id)) {
          return {
            ...message,
            replyTo: { ...message.replyTo, is_deleted: true, content: 'Message deleted' },
          };
        }

        return message;
      });
      this.selectedChat = {
        ...this.selectedChat,
        messages: updatedMessages,
      };

      this.selectedChatSubject.next(this.selectedChat);
    });

    this.messagingService.onPresenceList().subscribe((event) => {
      this.onlineUserIdsSubject.next(new Set(event.user_ids.map((id) => Number(id))));
    });

    this.messagingService.onUserOnline().subscribe((event) => {
      const ids = new Set(this.onlineUserIdsSubject.value);
      ids.add(Number(event.user_id));
      this.onlineUserIdsSubject.next(ids);
    });

    this.messagingService.onUserOffline().subscribe((event) => {
      const ids = new Set(this.onlineUserIdsSubject.value);
      ids.delete(Number(event.user_id));
      this.onlineUserIdsSubject.next(ids);
    });

    this.messagingService.onReconnect().subscribe((recovered) => {
      // Presence isn't covered by connection state recovery (it's a
      // request/response snapshot, not a room broadcast) — always refresh it.
      this.messagingService.requestPresenceList();

      // If Socket.io recovered the prior connection state, it already
      // rejoined every room we were in (including the open chat's and, for
      // conversation:updated, our own user:<id> room) and replayed anything
      // broadcast to them while we were disconnected straight into the
      // listeners below — nothing here would find anything new.
      if (recovered) {
        return;
      }

      if (this.currentUserId) {
        this.reloadConversations(this.currentUserId);
      }

      if (this.selectedChat && this.currentUserId) {
        this.messagingService.joinConversation(this.selectedChat.id);

        this.conversationService
          .getMessages(this.selectedChat.id, this.currentUserId)
          .subscribe((messages) => {
            const fetchedMessages = messages.map((msg) => ({
              ...msg,
              fromMe: msg.sender_id === this.currentUserId,
            }));

            const updatedChat = {
              ...this.selectedChat!,
              messages: this.mergeMessages(fetchedMessages, this.selectedChat!.messages),
            };

            this.selectedChat = updatedChat;

            this.selectedChatSubject.next(updatedChat);
          });
      }
    });

    this.messagingService.onMessage().subscribe((msg) => {
      const updatedChats = this.conversationSubject.value.map((chat) =>
        Number(chat.id) === Number(msg.conversation_id)
          ? {
              ...chat,
              lastMessage: msg.content,
              time: msg.created_at,
            }
          : chat,
      );

      this.conversationSubject.next(this.sortChatsByTime(updatedChats));

      if (!this.selectedChat) {
        return;
      }

      if (Number(this.selectedChat.id) !== Number(msg.conversation_id)) {
        return;
      }

      const alreadyExists = (this.selectedChat.messages || []).some(
        (m) => Number(m.id) === Number(msg.id),
      );

      if (alreadyExists) {
        return;
      }

      const message = {
        ...msg,
        fromMe: msg.sender_id === this.currentUserId,
      };

      const updatedChat = {
        ...this.selectedChat,
        messages: [...(this.selectedChat.messages || []), message],
      };

      this.selectedChat = updatedChat;

      this.selectedChatSubject.next(updatedChat);
    });

    this.messagingService.onConversationCreated().subscribe(() => {
      if (!this.currentUserId) return;
      this.reloadConversations(this.currentUserId);
    });

    this.messagingService.onMembersUpdated().subscribe((event) => {
      if (this.selectedChat && Number(this.selectedChat.id) === Number(event.conversation_id)) {
        let members = this.selectedChat.members || [];

        if (event.left_user_id !== undefined) {
          members = members.filter((member) => Number(member.id) !== Number(event.left_user_id));
        }

        if (event.added_members?.length) {
          const existingIds = new Set(members.map((member) => Number(member.id)));
          const newMembers = event.added_members.filter(
            (member) => !existingIds.has(Number(member.id)),
          );
          members = [...members, ...newMembers];
        }

        this.selectedChat = {
          ...this.selectedChat,
          members,
        };

        this.selectedChatSubject.next(this.selectedChat);
      }

      if (this.currentUserId) {
        this.reloadConversations(this.currentUserId);
      }
    });

    this.messagingService.onConversationRenamed().subscribe((event) => {
      if (this.selectedChat && Number(this.selectedChat.id) === Number(event.conversation_id)) {
        this.selectedChat = { ...this.selectedChat, name: event.name };
        this.selectedChatSubject.next(this.selectedChat);
      }

      if (this.currentUserId) {
        this.reloadConversations(this.currentUserId);
      }
    });

    this.messagingService.onMessageRead().subscribe((event) => {
      if (!this.selectedChat) {
        return;
      }

      if (Number(this.selectedChat.id) !== Number(event.conversation_id)) {
        return;
      }

      this.selectedChat = {
        ...this.selectedChat,
        messages: this.selectedChat.messages.map((message) => {
          const receiptsForMessage = event.receipts.filter(
            (receipt) => Number(receipt.message_id) === Number(message.id),
          );

          if (!receiptsForMessage.length) {
            return message;
          }

          const existingReceipts = message.MessageReadReceipts || [];

          const newReceipts = receiptsForMessage.filter(
            (receipt) =>
              !existingReceipts.some((existing) => Number(existing.id) === Number(receipt.id)),
          );

          return {
            ...message,
            MessageReadReceipts: [...existingReceipts, ...newReceipts],
          };
        }),
      };
      this.selectedChatSubject.next(this.selectedChat);
    });

    this.messagingService.onMessageError().subscribe((event) => {
      this.snackBar.open(event.error, 'Dismiss', { duration: 5000 });
    });
  }

  addGroupMembers(conversationId: number, userIds: number[], currentUserId: number): void {
    this.conversationService
      .addGroupMembers(conversationId, userIds, currentUserId)
      .subscribe(() => {
        this.reloadConversations(currentUserId);
      });
  }

  /**
   * Mutes or unmutes bell/toast notifications for one conversation. The list
   * flag flips immediately and rolls back if the server rejects it.
   */
  setMuted(chat: Chat, currentUserId: number, muted: boolean): void {
    // The list and the open thread hold separate copies of the chat, so both
    // need the flag, or the header toggle reads a stale value and can't flip back.
    const setFlag = (value: boolean) => {
      this.conversationSubject.next(
        this.conversationSubject.value.map((c) => (c.id === chat.id ? { ...c, muted: value } : c)),
      );
      if (this.selectedChat?.id === chat.id) {
        this.selectedChat = { ...this.selectedChat, muted: value };
        this.selectedChatSubject.next(this.selectedChat);
      }
    };

    setFlag(muted);
    this.conversationService.setConversationMuted(chat.id, currentUserId, muted).subscribe({
      error: () => setFlag(!muted),
    });
  }

  leaveConversation(chat: Chat, currentUserId: number): void {
    if (this.selectedChat?.id === chat.id) {
      this.selectedChat = undefined;
      this.selectedChatSubject.next(null);
    }
    this.conversationService.leaveConversation(chat.id, currentUserId).subscribe(() => {
      this.reloadConversations(currentUserId);
    });
  }

  /**
   * Renames a group conversation. Local state updates via the
   * conversation:renamed socket broadcast (which reaches the renamer too,
   * same as every other member), so nothing to do here on success — only
   * surface a message if the server rejects it (e.g. not the creator).
   */
  renameConversation(conversationId: number, name: string, currentUserId: number): void {
    this.conversationService.renameConversation(conversationId, name, currentUserId).subscribe({
      error: (err) => {
        this.snackBar.open(err?.error?.error || 'Failed to rename conversation.', 'Dismiss', {
          duration: 5000,
        });
      },
    });
  }
}
